import { useEffect, useRef, useState } from 'react'
import { X, CameraOff, Loader2 } from 'lucide-react'

type QRScannerModalProps = {
  isOpen: boolean
  onScan: (code: string) => void
  onClose: () => void
  title?: string
}

export function QRScannerModal({ isOpen, onScan, onClose, title = 'Scan QR Code' }: QRScannerModalProps) {
  const scannerRef = useRef<HTMLDivElement>(null)
  const html5QrCodeRef = useRef<any>(null)
  const scannedRef = useRef(false)
  const stoppedRef = useRef(false)
  const onScanRef = useRef(onScan)
  const [scannerError, setScannerError] = useState('')
  const [isInitializing, setIsInitializing] = useState(true)

  // Keep the latest onScan callback without re-triggering the scanner effect.
  // The parent passes a new inline function each render, so depending on it
  // directly would tear down and re-init the camera on every re-render.
  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  useEffect(() => {
    if (!isOpen) return
    let isMounted = true
    scannedRef.current = false
    stoppedRef.current = false
    setScannerError('')
    setIsInitializing(true)

    const safeStop = async () => {
      const instance = html5QrCodeRef.current
      if (!instance || stoppedRef.current) return
      try {
        const result = instance.stop()
        if (result && typeof result.then === 'function') {
          await result
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (!message.toLowerCase().includes('not running')) {
          console.warn('Failed to stop scanner:', message)
        }
      } finally {
        stoppedRef.current = true
      }
    }

    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode')
        if (!isMounted || !scannerRef.current) return

        const scannerId = `qr-scanner-${Date.now()}`
        scannerRef.current.id = scannerId

        const html5QrCode = new Html5Qrcode(scannerId)
        html5QrCodeRef.current = html5QrCode

        // Start the camera directly. Do NOT call getCameras() first — that opens
        // a second, redundant camera stream which races the start() stream and
        // can reject (camera busy / aborted) even when scanning succeeds, getting
        // mislabeled as a permission error. start() requests permission itself.
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            if (scannedRef.current) return
            scannedRef.current = true
            void safeStop().finally(() => {
              onScanRef.current(decodedText)
            })
          },
          () => {
            // ignore per-frame scan failures
          }
        )

        // Modal was closed (or remounted under StrictMode) while the camera was
        // starting — release it and don't surface anything.
        if (!isMounted) {
          void safeStop()
          return
        }
        setIsInitializing(false)
      } catch (err: any) {
        // Camera was released on purpose (successful scan / teardown) — not a real error.
        if (!isMounted || scannedRef.current) return
        const name = err?.name || ''
        const isPermission = name === 'NotAllowedError' || name === 'PermissionDeniedError'
        const noCamera = name === 'NotFoundError' || name === 'DevicesNotFoundError'
        console.error('Scanner init error:', err)
        setScannerError(
          isPermission
            ? 'Camera access was blocked. Please allow camera permissions in your browser and try again.'
            : noCamera
              ? 'No camera was found on this device.'
              : 'Unable to start the camera. Make sure no other app is using it, then try again.'
        )
        setIsInitializing(false)
      }
    }

    initScanner()

    return () => {
      isMounted = false
      void safeStop()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-120 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl bg-white dark:bg-neutral-900 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {isInitializing && (
            <div className="flex items-center justify-center py-8 text-neutral-500 dark:text-neutral-400">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Initializing camera...
            </div>
          )}

          {scannerError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-200 text-sm">
              <div className="flex items-start gap-2">
                <CameraOff className="w-4 h-4 mt-0.5" />
                <span>{scannerError}</span>
              </div>
            </div>
          )}

          <div className="rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800">
            <div ref={scannerRef} className="w-full h-[360px] bg-neutral-100 dark:bg-neutral-800" />
          </div>
        </div>
      </div>
    </div>
  )
}
